/**
 * Service de détection de l'état de l'API
 * Vérifie périodiquement si l'API est accessible
 */
class ApiStatusService {
  constructor() {
    this.isApiAvailable = false
    this.checkInterval = null
    this.listeners = []
    this.healthCheckDelay = 5000 // Délai fixe de 5 secondes
    this.consecutiveFailures = 0
  }

  /**
   * Démarre la vérification périodique de l'API avec backoff exponentiel
   */
  startChecking() {
    // Arrêter toute vérification en cours
    this.stopChecking()

    // Réinitialiser les compteurs
    this.healthCheckDelay = 5000
    this.consecutiveFailures = 0

    // Vérifier immédiatement (sans attendre le premier setTimeout)
    this.checkApiStatus().then(() => {
      this.scheduleNextHealthCheck()
    })
  }

  /**
   * Arrête la vérification périodique
   */
  stopChecking() {
    if (this.checkInterval) {
      clearTimeout(this.checkInterval)
      this.checkInterval = null
    }
  }

  /**
   * Planifie la prochaine vérification de santé avec le délai actuel
   */
  scheduleNextHealthCheck() {
    // Annuler toute vérification en attente
    if (this.checkInterval) {
      clearTimeout(this.checkInterval)
    }

    this.checkInterval = setTimeout(async () => {
      await this.checkApiStatus()
      // Planifier la prochaine vérification
      this.scheduleNextHealthCheck()
    }, this.healthCheckDelay)
  }

  /**
   * Vérifie si l'API est accessible
   * @returns {Promise<boolean>}
   */
  async checkApiStatus() {
    try {
      const { VUE_APP_API_URL } = await import('../utils/env')
      const healthEndpoint = `${VUE_APP_API_URL}/health`
      
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000)

      const response = await fetch(healthEndpoint, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
        // Mode no-cors si nécessaire pour éviter les problèmes CORS lors de tests
        mode: 'cors'
      })

      clearTimeout(timeoutId)

      const wasAvailable = this.isApiAvailable
      this.isApiAvailable = response.ok

      if (this.isApiAvailable) {
        // Succès : maintenir le délai à 20 secondes
        this.healthCheckDelay = 20000
        this.consecutiveFailures = 0
      } else {
        // Échec : maintenir le délai à 20 secondes (pas de backoff)
        this.healthCheckDelay = 20000
      }

      // Notifier les listeners UNIQUEMENT si le statut a changé (connectivité)
      // La synchronisation périodique est gérée de façon autonome par syncService.startPeriodicSync()
      if (wasAvailable !== this.isApiAvailable) {
        this.notifyListeners(this.isApiAvailable)
      }

      return this.isApiAvailable
    } catch (error) {
      // En cas d'erreur (timeout, network error, etc.), considérer l'API comme non disponible
      const wasAvailable = this.isApiAvailable
      this.isApiAvailable = false

      // Appliquer le backoff exponentiel
      this.applyBackoff()

      if (wasAvailable !== this.isApiAvailable) {
        this.notifyListeners(this.isApiAvailable)
      }

      return false
    }
  }

  /**
   * Applique le backoff exponentiel pour les vérifications échouées
   * Délai fixe de 5 secondes
   */
  applyBackoff() {
    this.consecutiveFailures++
    
    // Maintenir le délai fixe de 5 secondes
    this.healthCheckDelay = 5000
  }

  /**
   * Ajoute un listener pour être notifié des changements d'état
   * @param {Function} callback - Fonction appelée avec le nouveau statut (true/false)
   * @returns {Function} Fonction pour supprimer le listener
   */
  addListener(callback) {
    this.listeners.push(callback)

    // Notifier immédiatement avec le statut courant pour éviter l'angle mort
    // où un composant s'abonne après que le statut est déjà connu (true) :
    // sans cet appel, le listener ne serait jamais notifié car la condition
    // "wasAvailable !== isApiAvailable" ne serait plus jamais vraie.
    try {
      callback(this.isApiAvailable)
    } catch (error) {
      console.error('Erreur dans le listener API status (init):', error)
    }

    // Retourner une fonction pour supprimer le listener
    return () => {
      const index = this.listeners.indexOf(callback)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  /**
   * Notifie tous les listeners du changement d'état
   * @param {boolean} isAvailable - Nouveau statut de l'API
   */
  notifyListeners(isAvailable) {
    // Émettre sur le bus d'événements global (composants Vue branchés via Event.listen)
    if (typeof window !== 'undefined' && window.Event) {
      window.Event.fire('api-status-change', { isAvailable })
    }

    // Notifier les listeners internes (ex: mutationService)
    this.listeners.forEach(listener => {
      try {
        listener(isAvailable)
      } catch (error) {
        console.error('Erreur dans le listener API status:', error)
      }
    })
  }

  /**
   * Obtient le statut actuel de l'API
   * @returns {boolean}
   */
  getStatus() {
    return this.isApiAvailable
  }

  /**
   * Nettoie toutes les ressources
   */
  destroy() {
    this.stopChecking()
    this.listeners = []
  }
}

export default new ApiStatusService()
